import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { filter } from 'lodash';
import { Form, FormLabel, Row } from 'react-bootstrap';
import { selectedFieldSelector, updateSelectedField } from '../../../../../../slices/formadmin';
import CurrencyInput from '../../../../../../components/library/inputs/currency/CurrencyInput';
import { FormattedMessage, useIntl } from 'react-intl';
import CultureTextInput from '../../../../../../components/culture/CultureTextInput';
import { RootState } from '../../../../../../store';
import { ProductsModel } from '../../../../../../models/form/fields/products';
import { Product } from '../../../../../../models/form/product';
import NumberInput from '../../../../../../components/library/inputs/number/NumberInput';

const ProductsOptions: FC = () => {
  const field = useSelector(selectedFieldSelector) as ProductsModel;
  const dispatch = useDispatch();
  const intl = useIntl();
  const selectedLanguage = useSelector((state: RootState) => state.formAdmin.selectedLanguage) as string;
  const localizations = useSelector((state: RootState) => state.formAdmin.form?.localizations) as string[];

  const [products, setProducts] = useState<Product[]>([]);

  useEffect(() => {
    if (field.products) {
      setProducts(field.products);
    }
  }, [field]);

  const updateProduct = (product, index) => {
    const newProducts = [...products.slice(0, index), product, ...products.slice(index + 1)];
    setProducts(newProducts);
    dispatch(
      updateSelectedField({
        ...field,
        products: newProducts,
      }),
    );
  };

  const handleGoUp = (e: React.MouseEvent<Element, MouseEvent>, idx: number) => {
    if (idx > 0) {
      const tempProducts = [...products];
      const tempObj = tempProducts[idx - 1];
      tempProducts[idx - 1] = tempProducts[idx];
      tempProducts[idx] = tempObj;
      setProducts(tempProducts);
      dispatch(
        updateSelectedField({
          ...field,
          products: tempProducts,
        }),
      );
      e.preventDefault();
    }
  };
  const handleGoDown = (e: React.MouseEvent<Element, MouseEvent>, idx: number) => {
    if (idx < products.length - 1) {
      const tempProducts = [...products];
      const tempObj = tempProducts[idx + 1];
      tempProducts[idx + 1] = tempProducts[idx];
      tempProducts[idx] = tempObj;
      setProducts(tempProducts);
      dispatch(
        updateSelectedField({
          ...field,
          products: tempProducts,
        }),
      );
      e.preventDefault();
    }
  };

  return (
    <Form>
      <Row className="d-flex justify-content-center align-items-center">
        <table className="w-100 mb-4">
          {products?.length > 0 && (
            <thead>
              <tr>
                <th>
                  <FormLabel>
                    <FormattedMessage id="adminProperties.settings.options.order" />
                  </FormLabel>
                </th>
                <th>
                  <FormLabel>
                    <FormattedMessage id="adminProperties.settings.options.options" />
                  </FormLabel>
                </th>
                <th></th>
              </tr>
            </thead>
          )}
          <tbody>
            {products?.map((product, index) => {
              return (
                <tr className="option-row" key={product.value}>
                  <td className="sm-cell">
                    <button
                      className="btn btn-primary move-up"
                      onClick={(e: React.MouseEvent<Element, MouseEvent>) => handleGoUp(e, index)}
                    >
                      <span className="mdi mdi-arrow-up" />
                    </button>
                    <button
                      className=" btn btn-primary move-down"
                      onClick={(e: React.MouseEvent<Element, MouseEvent>) => handleGoDown(e, index)}
                    >
                      <span className="mdi mdi-arrow-down" />
                    </button>
                  </td>
                  <td className="lg-cell pb-2">
                    <div className="d-flex flex-column">
                      <div className="w-100 d-flex flex-row mb-2">
                        <div className="w-50 me-2">
                          <CultureTextInput
                            id={`field.${field.id}.option.${index}.name`}
                            culture={selectedLanguage}
                            localizations={localizations}
                            placeholder={'Name'}
                            value={product.text}
                            onChange={(e) => updateProduct({ ...product, text: e }, index)}
                          ></CultureTextInput>
                        </div>
                        <div className="w-50">
                          <CurrencyInput
                            placeholder={'Price'}
                            value={(product.price / 100).toFixed(2)}
                            onBlur={(e) => {
                              updateProduct({ ...product, price: Number(e.target.value) * 100 }, index);
                            }}
                          />
                        </div>
                      </div>
                      <div className="mb-2">
                        <CultureTextInput
                          id={`field.${field.id}.option.${index}.description`}
                          culture={selectedLanguage}
                          localizations={localizations}
                          placeholder={'Description'}
                          value={product.description}
                          onChange={(e) => updateProduct({ ...product, description: e }, index)}
                        ></CultureTextInput>
                      </div>
                      <div className="d-flex flex-row">
                        <div className="d-flex flex-row">
                          <div className="me-2" style={{ width: 120 }}>
                            <div className="form-group">
                              <NumberInput
                                id={`field.${field.id}.product.${index}.min`}
                                placeholder={'Qty Min'}
                                value={product.min}
                                onChange={(e) => updateProduct({ ...product, min: e.target.value }, index)}
                              ></NumberInput>
                            </div>
                          </div>
                          <div className="me-2" style={{ width: 120 }}>
                            <div className="form-group">
                              <NumberInput
                                id={`field.${field.id}.product.${index}.max`}
                                placeholder={'Qty Max'}
                                value={product.max}
                                onChange={(e) => updateProduct({ ...product, max: e.target.value }, index)}
                              ></NumberInput>
                            </div>
                          </div>
                        </div>
                        <div>
                          <div style={{ width: 120 }}>
                            <div className="form-group">
                              <NumberInput
                                id={`field.${field.id}.product.${index}.limit`}
                                placeholder={'Total Limit'}
                                value={product.limit}
                                onChange={(e) => updateProduct({ ...product, limit: e.target.value }, index)}
                              ></NumberInput>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="sm-cell pb-2">
                    <div
                      className="delete-btn ms-2"
                      onClick={() => {
                        const newProducts = filter(products, (item) => item !== product);
                        setProducts(newProducts);
                        dispatch(updateSelectedField({ ...field, products: newProducts }));
                      }}
                    >
                      <span className="mdi mdi-close"></span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <button
          className="w-50 btn btn-primary btn-block btn-lg"
          onClick={(e) => {
            const newProduct = new Product(localizations);
            const newProducts = [...products, { ...newProduct }];
            setProducts(newProducts);
            dispatch(updateSelectedField({ ...field, products: newProducts }));
            e.preventDefault();
          }}
        >
          {intl.formatMessage({ id: 'formAdmin.addButton' })}
        </button>
      </Row>
    </Form>
  );
};

export default ProductsOptions;
